import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { 
  Package, Plus, Search, RefreshCw, Edit, Trash2, Upload, 
  CheckCircle, XCircle, Filter
} from 'lucide-react';
import { toast } from 'sonner';

const CATEGORIES = ['medical', 'office', 'cleaning', 'other'];

export default function SupplyCatalog() {
  const { api } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [showInactive, setShowInactive] = useState(false);
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'medical',
    sku: '',
    default_unit: '',
    preferred_vendor: '',
    reorder_threshold: '',
    is_active: true
  });

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = { active_only: !showInactive };
      if (filterCategory !== 'all') params.category = filterCategory;
      if (searchQuery) params.search = searchQuery;
      
      const response = await api.get('/supplies/catalog', { params });
      setItems(response.data);
    } catch (error) {
      toast.error('Failed to fetch supply catalog');
    } finally {
      setLoading(false);
    }
  }, [api, showInactive, filterCategory, searchQuery]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      category: 'medical',
      sku: '',
      default_unit: '',
      preferred_vendor: '',
      reorder_threshold: '',
      is_active: true
    });
    setEditingItem(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name || '',
      description: item.description || '',
      category: item.category || 'medical',
      sku: item.sku || '',
      default_unit: item.default_unit || '',
      preferred_vendor: item.preferred_vendor || '',
      reorder_threshold: item.reorder_threshold?.toString() || '',
      is_active: item.is_active !== false
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);

    try {
      const payload = {
        ...formData,
        reorder_threshold: formData.reorder_threshold ? parseInt(formData.reorder_threshold) : null
      };

      if (editingItem) {
        await api.put(`/supplies/catalog/${editingItem.id}`, payload);
        toast.success('Supply item updated');
      } else {
        await api.post('/supplies/catalog', payload);
        toast.success('Supply item created');
      }
      
      setDialogOpen(false);
      resetForm();
      fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save supply item');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeactivate = async (item) => {
    if (!confirm(`Are you sure you want to deactivate "${item.name}"?`)) return;
    
    try {
      await api.delete(`/supplies/catalog/${item.id}`);
      toast.success('Supply item deactivated');
      fetchItems();
    } catch (error) {
      toast.error('Failed to deactivate item');
    }
  };

  const handleReactivate = async (item) => {
    try {
      await api.put(`/supplies/catalog/${item.id}`, { is_active: true });
      toast.success('Supply item reactivated');
      fetchItems();
    } catch (error) {
      toast.error('Failed to reactivate item');
    }
  };

  const filteredItems = items.filter(item => {
    const matchesSearch = 
      item.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.sku?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  const stats = {
    total: items.length,
    active: items.filter(i => i.is_active !== false).length,
    medical: items.filter(i => i.category === 'medical').length,
    office: items.filter(i => i.category === 'office').length,
    cleaning: items.filter(i => i.category === 'cleaning').length,
    other: items.filter(i => i.category === 'other').length
  };

  return (
    <div className="space-y-6" data-testid="supply-catalog-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Supply Catalog</h1>
          <p className="text-slate-500 mt-1">Manage the master list of supply items</p>
        </div>
        <Button 
          className="bg-teal-600 hover:bg-teal-700"
          onClick={openCreateDialog}
          data-testid="add-item-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Supply Item
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <Card>
          <CardContent className="pt-4 pb-4">
            <p className="text-sm text-slate-500">Total Items</p>
            <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <p className="text-sm text-slate-500">Active</p>
            <p className="text-2xl font-bold text-emerald-600">{stats.active}</p>
          </CardContent>
        </Card>
        <Card className={filterCategory === 'medical' ? 'ring-2 ring-teal-500' : ''} 
              onClick={() => setFilterCategory(filterCategory === 'medical' ? 'all' : 'medical')}>
          <CardContent className="pt-4 pb-4 cursor-pointer">
            <p className="text-sm text-slate-500">Medical</p>
            <p className="text-2xl font-bold text-blue-600">{stats.medical}</p>
          </CardContent>
        </Card>
        <Card className={filterCategory === 'office' ? 'ring-2 ring-teal-500' : ''} 
              onClick={() => setFilterCategory(filterCategory === 'office' ? 'all' : 'office')}>
          <CardContent className="pt-4 pb-4 cursor-pointer">
            <p className="text-sm text-slate-500">Office</p>
            <p className="text-2xl font-bold text-amber-600">{stats.office}</p>
          </CardContent>
        </Card>
        <Card className={filterCategory === 'cleaning' ? 'ring-2 ring-teal-500' : ''} 
              onClick={() => setFilterCategory(filterCategory === 'cleaning' ? 'all' : 'cleaning')}>
          <CardContent className="pt-4 pb-4 cursor-pointer">
            <p className="text-sm text-slate-500">Cleaning</p>
            <p className="text-2xl font-bold text-purple-600">{stats.cleaning}</p>
          </CardContent>
        </Card>
        <Card className={filterCategory === 'other' ? 'ring-2 ring-teal-500' : ''} 
              onClick={() => setFilterCategory(filterCategory === 'other' ? 'all' : 'other')}>
          <CardContent className="pt-4 pb-4 cursor-pointer">
            <p className="text-sm text-slate-500">Other</p>
            <p className="text-2xl font-bold text-slate-600">{stats.other}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters & Table */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search by name, description, or SKU..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-catalog"
              />
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Switch
                  checked={showInactive}
                  onCheckedChange={setShowInactive}
                  id="show-inactive"
                />
                <Label htmlFor="show-inactive" className="text-sm">Show Inactive</Label>
              </div>
              <Button variant="outline" size="sm" onClick={() => {
                setSearchQuery('');
                setFilterCategory('all');
                setShowInactive(false);
              }}>
                <Filter className="h-4 w-4 mr-2" />
                Clear Filters
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>SKU</TableHead>
                    <TableHead>Unit</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredItems.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-slate-500 py-8">
                        <Package className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                        <p>No supply items found</p>
                        <p className="text-sm">Add items to build your supply catalog</p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredItems.map((item) => (
                      <TableRow key={item.id} className={item.is_active === false ? 'opacity-50' : ''}>
                        <TableCell className="font-medium">{item.name}</TableCell>
                        <TableCell className="max-w-xs truncate">{item.description || '-'}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">{item.category}</Badge>
                        </TableCell>
                        <TableCell className="font-mono text-sm">{item.sku || '-'}</TableCell>
                        <TableCell>{item.default_unit || '-'}</TableCell>
                        <TableCell>
                          {item.is_active !== false ? (
                            <Badge className="bg-emerald-100 text-emerald-700">Active</Badge>
                          ) : (
                            <Badge variant="secondary">Inactive</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => openEditDialog(item)}
                              data-testid={`edit-item-${item.id}`}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            {item.is_active !== false ? (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleDeactivate(item)}
                                className="text-red-600 hover:text-red-700"
                                data-testid={`deactivate-item-${item.id}`}
                              >
                                <XCircle className="h-4 w-4" />
                              </Button>
                            ) : (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleReactivate(item)}
                                className="text-emerald-600 hover:text-emerald-700"
                                data-testid={`reactivate-item-${item.id}`}
                              >
                                <CheckCircle className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Edit Supply Item' : 'Add Supply Item'}</DialogTitle>
            <DialogDescription>
              {editingItem ? 'Update the supply item details' : 'Add a new item to the supply catalog'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
              <div className="space-y-2">
                <Label>Item Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Latex Examination Gloves"
                  required
                  data-testid="item-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Detailed description of the item..."
                  rows={2}
                  data-testid="item-description-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Category *</Label>
                  <Select 
                    value={formData.category} 
                    onValueChange={(value) => setFormData({ ...formData, category: value })}
                  >
                    <SelectTrigger data-testid="item-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(cat => (
                        <SelectItem key={cat} value={cat} className="capitalize">{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>SKU / Reference Code</Label>
                  <Input
                    value={formData.sku}
                    onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                    placeholder="e.g., MED-GLV-001"
                    data-testid="item-sku-input"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Default Unit</Label>
                  <Input
                    value={formData.default_unit}
                    onChange={(e) => setFormData({ ...formData, default_unit: e.target.value })}
                    placeholder="e.g., Box, Case, Each"
                    data-testid="item-unit-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Reorder Threshold</Label>
                  <Input
                    type="number"
                    value={formData.reorder_threshold}
                    onChange={(e) => setFormData({ ...formData, reorder_threshold: e.target.value })}
                    placeholder="e.g., 10"
                    min="0"
                    data-testid="item-threshold-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Preferred Vendor</Label>
                <Input
                  value={formData.preferred_vendor}
                  onChange={(e) => setFormData({ ...formData, preferred_vendor: e.target.value })}
                  placeholder="e.g., Medical Supplies Inc."
                  data-testid="item-vendor-input"
                />
              </div>
              
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                  id="is-active"
                />
                <Label htmlFor="is-active">Active (available for selection)</Label>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-teal-600 hover:bg-teal-700"
                disabled={formLoading}
                data-testid="save-item-btn"
              >
                {formLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : (editingItem ? 'Update' : 'Add Item')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
